import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMarkAllAsRead } from "@/api/notifications/useMarkAllAsRead";
import { useUnreadCount } from "@/api/notifications/useUnreadCount";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";

import { useNotificationsDrawer } from "./hooks/use-notifications-drawer";
import { NotificationsList } from "./NotificationsList";

const NotificationsDrawerComponent = (): React.JSX.Element => {
  const { t } = useTranslation("notifications");
  const { isOpen, closeDrawer } = useNotificationsDrawer();
  const [isOnlyUnread, setIsOnlyUnread] = useState(false);
  const { data: unreadCountData, isSuccess: isUnreadCountLoaded } =
    useUnreadCount({ enabled: isOpen });
  const markAllAsReadMutation = useMarkAllAsRead();

  // NOTE: NSAI-6008 — gate on a resolved count so a loading or failed query
  // hides "Mark all as read" instead of rendering it disabled (mirrors the
  // mobile fix from NSAI-4829).
  const hasUnread = isUnreadCountLoaded && (unreadCountData?.count ?? 0) > 0;

  const handleOpenChange = useCallback(
    (open: boolean): void => {
      if (!open) {
        closeDrawer();
      }
    },
    [closeDrawer],
  );

  const handleMarkAllAsRead = useCallback((): void => {
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="flex flex-col p-0 sm:max-w-md focus:outline-none focus-visible:outline-none"
        data-testid="notifications-drawer"
      >
        <SheetHeader className="px-4 pt-4 pb-3 border-b space-y-3">
          <SheetTitle>{t("title")}</SheetTitle>
          <div className="flex items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch
                checked={isOnlyUnread}
                onCheckedChange={setIsOnlyUnread}
                data-testid="notifications-only-unread"
              />
              {t("show_unread_only")}
            </label>
            {hasUnread && (
              <Button
                variant="ghost"
                size="sm"
                disabled={markAllAsReadMutation.isPending}
                onClick={handleMarkAllAsRead}
                data-testid="notifications-mark-all"
              >
                {t("mark_all_read")}
              </Button>
            )}
          </div>
        </SheetHeader>
        <ScrollArea className="flex-1">
          <NotificationsList
            isOpen={isOpen}
            onlyUnread={isOnlyUnread}
            onCloseDrawer={closeDrawer}
          />
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export const NotificationsDrawer = memo(NotificationsDrawerComponent);
